<template>
  <div class="zu-table-wrapper" :class="alignClass" :style="newStyle">
    <div v-if="$slots.header" class="zu-table-header">
      <slot name="header"></slot>
    </div>
    <div class="zu-table-body" :style="comBodyStyle">
      <div
        v-for="(item, idx) in list"
        :key="idx"
        class="zu-table-row"
        :class="item.class"
      >
        <div class="zu-table-cell">
          <div class="zu-table-title" :style="{ ...comLabelStyle, ...item.labelStyle }">
            <div class="zu-table-text" v-ellipsis-tooltip>
              <span>{{ item.label }}</span>
            </div>
          </div>
          <div class="zu-table-content" :style="{ ...comValueStyle, ...item.valueStyle}">
            <div class="zu-table-text" v-ellipsis-tooltip>
              <span>{{ item.value }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="$slots.footer" class="zu-table-footer">
      <slot name="footer"></slot>
    </div>
  </div>
</template>

<script>
import ellipsisTooltip from "@/directive/ellipsisTooltip";
const alignMap = ['left', 'center', 'right']
export default {
  name: "ZuTable",
  props: {
    list: { type: Array, default: () => [] },
    column: { type: [Number, String], default: 3 },
    min: { type: [Number, String], default: 0 },
    align: { type: String, default: 'center' },
    color: { type: String, default: '' },
    bgColor: { type: String, default: '' },
    labelWidth: { type: String, default: '' },
    labelAlign: { type: String, default: '' },
    labelColor: { type: String, default: '' },
    labelBgColor: { type: String, default: '' },
    valueAlign: { type: String, default: '' },
    valueColor: { type: String, default: '' },
    valueBgColor: { type: String, default: '' },
    cssStyle: { type: Object, default: () => ({}) },
    labelStyle: { type: Object, default: () => ({}) },
    valueStyle: { type: Object, default: () => ({}) }
  },
  directives: {
    ellipsisTooltip
  },
  computed: {
    alignClass() {
      let str = ''
      if (alignMap.includes(this.align)) str += ` zu-table-${this.align}`
      if (alignMap.includes(this.labelAlign)) str += ` zu-table-label-${this.labelAlign}`
      if (alignMap.includes(this.valueAlign)) str += ` zu-table-value-${this.valueAlign}`
      return str
    },
    newStyle() {
      const data = {}
      const color = this.color
      const bgColor = this.bgColor
      if (color) data['color'] = color
      if (bgColor)  data['background-color'] = bgColor
      return {
        ...data,
        ...this.cssStyle
      }
    },
    comBodyStyle() {
      const data = {}
      const col = Number(this.column) || 0
      const min = Number(this.min) || 300
      data['grid-template-columns'] = col ? `repeat(${col}, 1fr)` : `repeat(auto-fill, minmax(${min}rem, 1fr))`
      return {
        ...data,
      }
    },
    comLabelStyle() {
      const data = {}
      const labelWidth = this.labelWidth
      const labelColor = this.labelColor
      const labelBgColor = this.labelBgColor
      if (labelWidth) data['width'] = labelWidth
      if (labelColor) data['color'] = labelColor
      if (labelBgColor) data['background-color'] = labelBgColor
      return {
        ...data,
        ...this.labelStyle
      }
    },
    comValueStyle() {
      const data = {}
      const valueColor = this.valueColor
      const valueBgColor = this.valueBgColor
      if (valueColor) data['color'] = valueColor
      if (valueBgColor) data['background-color'] = valueBgColor
      return {
        ...data,
        ...this.valueStyle
      }
    }
  },
}
</script>

<style lang="scss" scoped>
.zu-table {
  &-wrapper {
    width: 100%;
    border-collapse: collapse;
    border-top: solid 1rem #E7E7E7;
    border-left: solid 1rem #E7E7E7;
    background-color: #FBFCFC;
    box-sizing: border-box;
    &.zu-table-left {
      .zu-table-title,
      .zu-table-content {
        justify-content: flex-start;
        text-align: left;
      }
    }
    &.zu-table-center {
      .zu-table-title,
      .zu-table-content {
        justify-content: center;
        text-align: center;
      }
    }
    &.zu-table-right {
      .zu-table-title,
      .zu-table-content {
        justify-content: flex-end;
        text-align: right;
      }
    }
    &.zu-table-label-left {
      .zu-table-title {
        justify-content: flex-start;
        text-align: left;
      }
    }
    &.zu-table-label-center {
      .zu-table-title {
        justify-content: center;
        text-align: center;
      }
    }
    &.zu-table-label-right {
      .zu-table-title {
        justify-content: flex-end;
        text-align: right;
      }
    }
    &.zu-table-value-left {
      .zu-table-content {
        justify-content: flex-start;
        text-align: left;
      }
    }
    &.zu-table-value-center {
      .zu-table-content {
        justify-content: center;
        text-align: center;
      }
    }
    &.zu-table-value-right {
      .zu-table-content {
        justify-content: flex-end;
        text-align: right;
      }
    }
  }

  &-header,
  &-footer {
    font-size: 14rem;
    color: #333;
    border-bottom: solid 1rem #E7E7E7;
    border-right: solid 1rem #E7E7E7;
  }

  &-body {
    font-size: 14rem;
    color: #333;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
  }

  &-row {
    border-bottom: solid 1rem #E7E7E7;
    border-right: solid 1rem #E7E7E7;
    &.full {
      grid-column: 1 / -1;
    }
    &.left {
      .zu-table-content {
        justify-content: flex-start;
        text-align: left;
      }
    }
    &.center {
      .zu-table-content {
        justify-content: center;
        text-align: center;
      }
    }
    &.right {
      .zu-table-content {
        justify-content: flex-end;
        text-align: right;
      }
    }
  }

  &-cell {
    height: 100%;
    display: flex;
    // align-items: center;
    justify-content: center;
    line-height: 20rem;
    text-align: center;
  }

  &-title {
    width: 110rem;
    min-height: 48rem;
    padding: 4rem 5rem;
    background-color: #F6F9FF;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-right: solid 1rem #E7E7E7;
  }

  &-content {
    padding: 4rem 16rem;
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  &-text {
    overflow: hidden;
    display: -webkit-box;
    box-orient: vertical;
    -webkit-box-orient: vertical;
    line-clamp: 2;
    -webkit-line-clamp: 2;
  }
}
</style>
